import { auth, db } from "@/src/lib/firebase";
import { mapPublicAccount, mapPublicProfile } from "@/src/lib/publicProfiles";
import type { PublicProfile } from "@/src/types/models";
import { doc, onSnapshot } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState } from "react";

export function usePublicProfile(uid?: string | null) {
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState<"not-found" | "load-failed" | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const profileId = useMemo(() => String(uid ?? "").trim(), [uid]);
  const viewerUid = auth.currentUser?.uid ?? null;
  useEffect(() => {
    if (!profileId) {
      setProfile(null);
      setError("not-found");
      setLoadingProfile(false);
      return;
    }

    setLoadingProfile(true);
    setError(null);

    let alive = true;
    let legacyUnsub: (() => void) | null = null;

    const finishNotFound = () => {
      if (!alive) return;
      setProfile(null);
      setError("not-found");
      setLoadingProfile(false);
    };

    const subscribeLegacy = () => {
      if (!alive || legacyUnsub) return;
      const collectionName = viewerUid && profileId === viewerUid ? "users" : "publicProfiles";
      legacyUnsub = onSnapshot(
        doc(db, collectionName, profileId),
        (snap) => {
          if (!alive) return;
          if (!snap.exists()) {
            finishNotFound();
            return;
          }
          setProfile(mapPublicProfile(snap.id, snap.data()));
          setError(null);
          setLoadingProfile(false);
        },
        () => {
          if (!alive) return;
          setProfile(null);
          setError("load-failed");
          setLoadingProfile(false);
        }
      );
    };

    const accountUnsub = onSnapshot(
      doc(db, "accounts", profileId),
      (snap) => {
        if (!alive) return;
        if (snap.exists() && snap.data()?.status === "active") {
          setProfile(mapPublicAccount(snap.id, snap.data()));
          setError(null);
          setLoadingProfile(false);
          return;
        }
        subscribeLegacy();
      },
      () => subscribeLegacy()
    );

    return () => {
      alive = false;
      accountUnsub();
      legacyUnsub?.();
    };
  }, [profileId, retryKey, viewerUid]);

  const retry = useCallback(() => {
    setRetryKey((value) => value + 1);
  }, []);

  return { profile, loadingProfile, error, retry };
}
